import { eventChannel } from 'redux-saga';
import type { EventChannel } from 'redux-saga';
import { call, getContext, put, takeEvery } from 'redux-saga/effects';
import type { AuthEvent, OAuth2Service } from 'src/services/OAuth2Service';
import { container } from 'src/StaticContainer';
import {
    setAccessToken,
    setIdToken,
    setRefreshToken,
    setUserDetails,
    clearAccessToken,
    clearRefreshToken,
} from 'src/store/actions/auth';
import type { SagaContext } from 'src/store/types';

const log = container.get('Logger').getSubLogger({ name: 'tokenSync' }, { component: 'auth' });

/**
 * Creates an event channel that bridges OAuth service events to Redux saga
 */
function createAuthEventChannel(oauth2Service: OAuth2Service): EventChannel<AuthEvent> {
    if (TRACE) {
        log.trace('Creating auth event channel for managed OAuth service');
    }

    return eventChannel((emitter) => {
        const unsubscribe = oauth2Service.subscribeToAuthEvents((event) => {
            if (TRACE) {
                log.trace('Auth event received', { event });
            }
            emitter(event);
        });

        // Return unsubscribe function for cleanup
        return () => {
            if (TRACE) {
                log.trace('Unsubscribing from auth events');
            }
            unsubscribe();
        };
    });
}

/**
 * Handles individual auth events and updates Redux state accordingly
 */
function* handleAuthEvent(event: AuthEvent) {
    if (TRACE) {
        log.trace('Handling auth event', { eventType: event.type });
    }

    switch (event.type) {
        case 'TOKEN_REFRESHED': {
            // Update Redux with new tokens
            if (event.payload?.tokens) {
                const { accessToken, idToken, refreshToken } = event.payload.tokens;

                if (TRACE) {
                    log.trace({
                        message: 'tokenSync: TOKEN_REFRESHED received',
                        hasAccessToken: typeof accessToken === 'string',
                        accessTokenLength: typeof accessToken === 'string' ? accessToken.length : 0,
                        hasIdToken: typeof idToken === 'string',
                        hasRefreshToken: typeof refreshToken === 'string',
                    });
                }

                // Only dispatch if we have a valid access token string
                // Invalid tokens can occur during expired session scenarios
                if (typeof accessToken === 'string' && accessToken.length > 0) {
                    yield put(
                        setAccessToken({
                            token: accessToken,
                            expires: undefined, // Managed service handles expiration internally
                        }),
                    );
                    log.debug('Access token refreshed and Redux state updated');
                } else {
                    log.warn('TOKEN_REFRESHED event received without valid access token', {
                        accessTokenType: typeof accessToken,
                    });
                }

                // ID token is optional but must be valid if present
                if (typeof idToken === 'string' && idToken.length > 0) {
                    yield put(
                        setIdToken({
                            token: idToken,
                            expires: undefined,
                        }),
                    );
                }

                // Refresh token is optional but must be valid if present
                if (typeof refreshToken === 'string' && refreshToken.length > 0) {
                    yield put(
                        setRefreshToken({
                            token: refreshToken,
                            expires: undefined,
                        }),
                    );
                }
            }
            break;
        }

        case 'SESSION_EXPIRED': {
            // Clear tokens from Redux when session expires
            yield put(clearAccessToken('Session expired'));
            yield put(clearRefreshToken('Session expired'));
            yield put(setIdToken(null)); // ID token is cleared by setting to null

            log.info('Session expired, tokens cleared from Redux');
            break;
        }

        case 'SESSION_RESTORED': {
            // Session restored from another tab/window
            // The managed service will emit TOKEN_REFRESHED with the restored tokens
            // so we don't need to do anything here except log
            log.info('Session restored from another tab/window');
            break;
        }

        case 'LOGOUT': {
            // Clear all auth state on logout
            yield put(clearAccessToken('User logged out'));
            yield put(clearRefreshToken('User logged out'));
            yield put(setIdToken(null)); // ID token is cleared by setting to null
            yield put(setUserDetails(undefined)); // User details are cleared by setting to undefined

            log.info('User logged out, all auth state cleared');
            break;
        }

        default: {
            // Exhaustive check - should never reach here with proper typing
            const exhaustiveCheck: never = event;
            log.warn('Unhandled auth event type', { event: exhaustiveCheck });
        }
    }
}

/**
 * Main saga that watches for auth events from the OAuth service
 */
export function* watchAuthEvents() {
    try {
        const oauth2Service: SagaContext['oAuth2Client'] = yield getContext('oAuth2Client');

        log.info('Starting auth event watcher');

        // Create the event channel
        const authEventChannel: EventChannel<AuthEvent> = yield call(createAuthEventChannel, oauth2Service);

        // Watch for events from the channel
        yield takeEvery(authEventChannel, handleAuthEvent);

        log.info('Auth event watcher started successfully');
    } catch (error) {
        log.error('Error in watchAuthEvents saga', { error });
    }
}
